import type { DemoDomain } from './demoCenter'

export type ScaleId = 'individual' | 'organizational' | 'municipal' | 'national'

export type ScalePageContent = {
  id: ScaleId
  path: string
  title: string
  lead: string
  intro: string[]
  sections: { title: string; paragraphs: string[] }[]
  /** Shown when the page discusses aggregate patterns. */
  aggregate?: boolean
  /** Shown when the page discusses institutional outcomes. */
  institutional?: boolean
  related: { label: string; href: string; demo?: DemoDomain['id'] }[]
}

export const scalePages: Record<ScaleId, ScalePageContent> = {
  individual: {
    id: 'individual',
    path: '/individual-level',
    title: 'Individual Level',
    lead: 'A proposed application of NCJI within HCF, designed to help people understand how they reason, where their strengths lie and which capabilities are most worth strengthening.',
    intro: [
      'The purpose at this level is not to classify a person. It is to create a clearer picture of development: translating findings into growth pathways, identifying where support may be needed and connecting people with realistic routes for progress across education, work, civic participation and leadership.',
    ],
    sections: [
      {
        title: 'Self-Awareness and Capability Insight',
        paragraphs: [
          'Feedback tied to specific tasks is designed to help individuals see how they evaluate evidence, respond under pressure, plan and recognize manipulation. A pilot would examine whether this feedback produces more accurate self-understanding.',
        ],
      },
      {
        title: 'From assessment to development',
        paragraphs: [
          'A core principle of NCJI is that every meaningful weakness should be paired with a pathway. The framework should not stop at showing where someone is underdeveloped; it should clarify what stronger readiness would require and which support, tools or experiences could help build it.',
        ],
      },
      {
        title: 'Opportunity Guidance',
        paragraphs: [
          'Many people have real strengths that are not connected to the right opportunities or support. Guidance could point toward mentorship, training, structured experience or education. Guidance is separate from eligibility: any use for role qualification is a separate proposed use requiring evidence for that role and human review.',
        ],
      },
      {
        title: 'Progress Over Time',
        paragraphs: [
          'NCJI is designed to support movement, not a fixed label. Reassessment would show change over time; an absence of evidence is recorded as insufficient evidence, not low performance.',
        ],
      },
    ],
    related: [
      { label: 'Education demo', href: '/education', demo: 'education' },
      { label: 'Community demo', href: '/civil-society', demo: 'community' },
    ],
  },
  organizational: {
    id: 'organizational',
    path: '/organizational-level',
    title: 'Organizational Level',
    lead: 'A proposed application of NCJI within HCF, designed to help institutions understand and develop the human capabilities that shape performance, leadership development and resilience.',
    intro: [
      'Rather than looking only at outputs or credentials, the framework is designed to make reasoning, self-direction, emotional regulation, cooperation and responsibility more visible and more developable.',
    ],
    sections: [
      {
        title: 'Readiness Inside Institutions',
        paragraphs: [
          'Development records could help organizations see where readiness is strong and where it is still forming, informing role preparation, advancement and succession. Advancement decisions remain with authorized people.',
        ],
      },
      {
        title: 'From assessment to development',
        paragraphs: [
          'Findings would connect to coaching, training, mentorship and structured responsibility. A pilot would examine which activities lead to lasting improvement.',
        ],
      },
      {
        title: 'Leadership Pipelines and Responsibility',
        paragraphs: [
          'As people move into roles with greater consequence, exercises could focus on consequence awareness, communication under pressure and responsibility at scale. Role qualification is a separate proposed use requiring evidence for the particular role and human review.',
        ],
      },
      {
        title: 'Institutional Resilience',
        paragraphs: [
          'Aggregate development data could help identify where support systems or leadership depth are thin, informing continuity planning.',
        ],
      },
    ],
    aggregate: true,
    institutional: true,
    related: [
      { label: 'Talent demo', href: '/employers', demo: 'talent' },
      { label: 'Leadership demo', href: '/leadership', demo: 'leadership' },
    ],
  },
  municipal: {
    id: 'municipal',
    path: '/municipal-scale',
    title: 'Municipal Scale',
    lead: 'A proposed application of NCJI within HCF, designed to help local leaders and community systems understand patterns in local needs, opportunity pathways and participation.',
    intro: [
      'Municipal systems are closest to the conditions people experience day to day. Aggregate views could help local institutions see where youth pathways are thin, where support systems are fragmented and where people report barriers to participation and access to support.',
    ],
    sections: [
      {
        title: 'Local Civic Resilience',
        paragraphs: [
          'Programs could offer practice in evaluating evidence, recognizing manipulation and cooperating constructively. A pilot would examine whether such programs make a measurable local difference.',
        ],
      },
      {
        title: 'Youth Pathways and Opportunity Access',
        paragraphs: [
          'Aggregate data could show where local opportunity alignment is weak and where community systems could do more to connect young people with education, mentorship, work and civic participation. Youth applications would need age-appropriate methods.',
        ],
      },
      {
        title: 'Community Strengthening',
        paragraphs: [
          'Municipal strength depends on local trust, functioning support systems and pathways into constructive roles. Reported service gaps and barriers could be reviewed alongside program outcomes.',
        ],
      },
      {
        title: 'Local Leadership and Coordination',
        paragraphs: [
          'Leadership development could help cultivate local leaders over time. Any use for role qualification would require evidence for the particular role and human review.',
        ],
      },
    ],
    aggregate: true,
    institutional: true,
    related: [
      { label: 'Government demo', href: '/government', demo: 'government' },
      { label: 'Community demo', href: '/civil-society', demo: 'community' },
    ],
  },
  national: {
    id: 'national',
    path: '/national-scale',
    title: 'National Scale',
    lead: 'A proposed application of NCJI within HCF, designed to support national planning for development, public services and institutional strengthening.',
    intro: [
      'National systems often address problems at the policy level without a structured view of the needs and capabilities beneath them. Aggregate views could inform that planning alongside data on services, resources and conditions.',
    ],
    sections: [
      {
        title: 'Strategic Planning for Civic Resilience',
        paragraphs: [
          'Planners could examine patterns such as misinformation exposure, reported barriers to participation and access to support, and opportunity alignment across regions. These patterns describe populations and conditions; they are not NCJI measurements of individuals.',
        ],
      },
      {
        title: 'Institutional Strengthening',
        paragraphs: [
          'Development programs could support civil-service modernization and continuity. A pilot would examine whether they improve institutional performance alongside changes in resources and processes.',
        ],
      },
      {
        title: 'Leadership Qualification as a Separate Proposed Use',
        paragraphs: [
          'Leadership exercises could support development for senior public roles. Using any finding to qualify someone for a role is a separate proposed use requiring evidence for that particular role, an explanation of findings, human review and a route to reassessment.',
        ],
      },
      {
        title: 'National Capability Mapping',
        paragraphs: [
          'Aggregate views across regions and institutions could show workforce-readiness trends, opportunity bottlenecks and gaps in support, informing long-range strategy.',
        ],
      },
    ],
    aggregate: true,
    institutional: true,
    related: [{ label: 'Government demo', href: '/government', demo: 'government' }],
  },
}
